import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  POSTPROCESSORS,
  defaultOptions,
  type ExportArtifact,
  type OptionValues,
  type Postprocessor,
} from "@/lib/exporters";
import { useExportContext } from "@/lib/exporters/useExportContext";
import { getToken } from "@/lib/ddb";

const CATEGORY_LABEL: Record<Postprocessor["category"], string> = {
  cut_list: "Cut list",
  drill:    "Drilling",
  drawing:  "Drawings",
  machine:  "CNC native",
};

/** A machine profile shipped under VFS /postprocessors/*.json. The SPA
 *  reads them on Export-panel mount and lets the user pick one; selecting
 *  a profile overlays its `defaults.<postprocessor_id>` onto each
 *  postprocessor's option set. Shops customise/add files in VFS without
 *  rebuilding the SPA. */
interface MachineProfile {
  id: string;
  label: string;
  vendor?: string;
  description?: string;
  defaults: Record<string, Partial<OptionValues>>;
}

async function fetchProfiles(): Promise<MachineProfile[]> {
  const tok = getToken();
  const headers = tok ? { Authorization: `Bearer ${tok}` } : undefined;
  const listRes = await fetch("/db/default/fs/postprocessors/", { headers });
  if (!listRes.ok) return [];
  const list = await listRes.json() as { entries: Array<{ name: string; type: string }> };
  const files = list.entries.filter(e => e.type === "file" && e.name.endsWith(".json"));
  const out: MachineProfile[] = [];
  for (const f of files) {
    const r = await fetch(`/db/default/fs/postprocessors/${encodeURIComponent(f.name)}`, { headers });
    if (!r.ok) continue;
    try { out.push(await r.json() as MachineProfile); }
    catch { /* skip malformed */ }
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

export function ExportPanel() {
  const { id } = useParams();
  const nav = useNavigate();
  const numId = id ? Number(id) : null;
  const { ctx, isLoading, error } = useExportContext(numId);

  // Per-postprocessor option state — initialised from defaults, persists
  // across regenerations within a single panel open.
  const [opts, setOpts] = useState<Record<string, OptionValues>>(() => {
    const out: Record<string, OptionValues> = {};
    for (const pp of POSTPROCESSORS) out[pp.id] = defaultOptions(pp);
    return out;
  });

  // Machine profiles loaded from VFS /postprocessors/.
  const [profiles, setProfiles] = useState<MachineProfile[]>([]);
  const [activeProfile, setActiveProfile] = useState<string>("");
  useEffect(() => {
    let cancelled = false;
    fetchProfiles().then((ps) => { if (!cancelled) setProfiles(ps); });
    return () => { cancelled = true; };
  }, []);

  function applyProfile(profileId: string) {
    setActiveProfile(profileId);
    if (!profileId) {
      // Reset to base defaults
      const reset: Record<string, OptionValues> = {};
      for (const pp of POSTPROCESSORS) reset[pp.id] = defaultOptions(pp);
      setOpts(reset);
      return;
    }
    const prof = profiles.find(p => p.id === profileId);
    if (!prof) return;
    setOpts((current) => {
      const next: Record<string, OptionValues> = { ...current };
      for (const pp of POSTPROCESSORS) {
        const overlay = prof.defaults[pp.id];
        if (!overlay) continue;
        next[pp.id] = { ...defaultOptions(pp), ...overlay } as OptionValues;
      }
      return next;
    });
  }

  // Most recent generation status per postprocessor (last download / error).
  const [status, setStatus] = useState<Record<string, { ok: boolean; msg: string }>>({});

  const byCategory = useMemo(() => {
    const groups: Record<string, Postprocessor[]> = {};
    for (const pp of POSTPROCESSORS) {
      (groups[pp.category] ??= []).push(pp);
    }
    return groups;
  }, []);

  function generate(pp: Postprocessor) {
    if (!ctx) return;
    try {
      const artifacts = pp.generate(ctx, opts[pp.id]!);
      for (const a of artifacts) downloadArtifact(a);
      setStatus((s) => ({ ...s, [pp.id]: { ok: true, msg: `Downloaded ${artifacts.map((a) => a.filename).join(", ")}` } }));
    } catch (e) {
      setStatus((s) => ({ ...s, [pp.id]: { ok: false, msg: e instanceof Error ? e.message : String(e) } }));
    }
  }

  if (!id || Number.isNaN(Number(id))) return <div className="empty-state">Invalid project id.</div>;
  if (isLoading) return <div className="empty-state">Loading project…</div>;
  if (error) return <div className="empty-state">Couldn't load export inputs: {error.message}</div>;
  if (!ctx) return <div className="empty-state">No data.</div>;

  const ncab = ctx.cabinets.length;
  const npart = ctx.parts.reduce((a, c) => a + c.parts.reduce((b, p) => b + (p.qty ?? 1), 0), 0);

  return (
    <div style={{ padding: "16px 24px", display: "grid", gridTemplateRows: "auto 1fr", height: "100%", gap: 16 }}>
      <header style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Export</h2>
        <span className="muted">·</span>
        <span className="muted">{ctx.project.name}</span>
        <span className="muted">· {ncab} cabinets · {npart} parts</span>
        <span style={{ flex: 1 }} />
        {profiles.length > 0 && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
            <span className="muted">Machine profile</span>
            <select
              value={activeProfile}
              onChange={(e) => applyProfile(e.target.value)}
              title={profiles.find(p => p.id === activeProfile)?.description ?? "Overlay vendor defaults on every postprocessor"}
            >
              <option value="">— none —</option>
              {profiles.map(p => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
          </label>
        )}
        <button className="ghost" onClick={() => nav(`/projects/${id}/design`)}>← Back to design</button>
      </header>

      <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 24 }}>
        {ncab === 0 && (
          <div className="muted">Project has no cabinets. Add some in the design view to enable exports.</div>
        )}
        {Object.entries(byCategory).map(([cat, items]) => (
          <section key={cat}>
            <h3 style={{ margin: "0 0 12px", fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--fg-dim)" }}>
              {CATEGORY_LABEL[cat as Postprocessor["category"]] ?? cat}
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 12 }}>
              {items.map((pp) => (
                <PostprocessorCard
                  key={pp.id}
                  pp={pp}
                  opts={opts[pp.id]!}
                  setOpts={(next) => setOpts((s) => ({ ...s, [pp.id]: next }))}
                  status={status[pp.id]}
                  disabled={ncab === 0}
                  onGenerate={() => generate(pp)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

// ── Card ─────────────────────────────────────────────────────────

interface CardProps {
  pp: Postprocessor;
  opts: OptionValues;
  setOpts: (next: OptionValues) => void;
  status?: { ok: boolean; msg: string };
  disabled: boolean;
  onGenerate: () => void;
}

function PostprocessorCard({ pp, opts, setOpts, status, disabled, onGenerate }: CardProps) {
  return (
    <div className="card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <h4 style={{ margin: 0, fontSize: 14 }}>{pp.label}</h4>
          <span className="muted" style={{ fontSize: 11 }}>.{pp.extension}</span>
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{pp.description}</div>
      </div>

      {pp.options.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {pp.options.map((o) => (
            <OptionField key={o.key} option={o} value={opts[o.key]!} onChange={(v) => setOpts({ ...opts, [o.key]: v })} />
          ))}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button onClick={onGenerate} disabled={disabled}>Generate</button>
        {status && (
          <span
            style={{ fontSize: 12, color: status.ok ? "var(--ok)" : "var(--danger)" }}
            title={status.msg}
          >
            {status.ok ? "✓ " : "✗ "}{truncate(status.msg, 48)}
          </span>
        )}
      </div>
    </div>
  );
}

function OptionField({
  option,
  value,
  onChange,
}: {
  option: Postprocessor["options"][number];
  value: string | number | boolean;
  onChange: (v: string | number | boolean) => void;
}) {
  if (option.type === "select") {
    return (
      <div>
        <label className="label">{option.label}</label>
        <select value={String(value)} onChange={(e) => onChange(e.target.value)}>
          {option.choices?.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        {option.help && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{option.help}</div>}
      </div>
    );
  }
  if (option.type === "boolean") {
    return (
      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
        <input
          type="checkbox"
          style={{ width: "auto" }}
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span style={{ fontSize: 13 }}>{option.label}</span>
        {option.help && <span className="muted" style={{ fontSize: 11 }}>· {option.help}</span>}
      </label>
    );
  }
  // number
  return (
    <div>
      <label className="label">{option.label}</label>
      <input
        type="number"
        value={Number(value)}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

// ── Browser download ─────────────────────────────────────────────

function downloadArtifact(a: ExportArtifact): void {
  // TS 5+ types Uint8Array as `Uint8Array<ArrayBufferLike>` (allowing SharedArrayBuffer),
  // but Blob's BlobPart wants `Uint8Array<ArrayBuffer>`. Coerce — our content
  // never originates from a SAB, the dom.lib stub is just overly cautious.
  const blob = new Blob([a.content as BlobPart], { type: a.mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = a.filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
